import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { buildTournamentPublicUrl } from '../../utils/urlBuilder';

export interface TournamentShareModalProps {
  open: boolean;
  onClose: () => void;
  torneioId: string;
  torneioNome?: string;
}

const TournamentShareModal: React.FC<TournamentShareModalProps> = ({
  open,
  onClose,
  torneioId,
  torneioNome,
}) => {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const tournamentUrl = buildTournamentPublicUrl(torneioId);

  const handleCopy = async () => {
    try {
      setCopyError(false);
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(tournamentUrl);
      } else {
        // Fallback para navegadores sem suporte à Clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = tournamentUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Erro ao copiar link:', error);
      setCopyError(true);
      setTimeout(() => setCopyError(false), 2000);
    }
  };

  const handleOpenPage = () => {
    window.open(tournamentUrl, '_blank', 'noopener,noreferrer');
  };

  const handleClose = () => {
    setCopied(false);
    setCopyError(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="span">
          Compartilhar Torneio
        </Typography>
        <IconButton onClick={handleClose} size="small" aria-label="fechar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {torneioNome && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {torneioNome}
          </Typography>
        )}

        <TextField
          fullWidth
          variant="outlined"
          value={tournamentUrl}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleCopy} edge="end" aria-label="copiar link">
                  {copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
        />

        {copied && (
          <Typography variant="caption" color="success.main">
            Link copiado!
          </Typography>
        )}

        {copyError && (
          <Typography variant="caption" color="error.main">
            Erro ao copiar. Selecione o link manualmente.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          onClick={handleOpenPage}
          startIcon={<OpenInNewIcon />}
        >
          Abrir página
        </Button>
        <Button
          variant="contained"
          onClick={handleCopy}
          startIcon={copyError ? <ErrorOutlineIcon /> : copied ? <CheckIcon /> : <ContentCopyIcon />}
          color={copyError ? 'error' : copied ? 'success' : 'primary'}
        >
          {copyError ? 'Erro!' : copied ? 'Copiado!' : 'Copiar link'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TournamentShareModal;
